/**
 * ResetPassword - the page the emailed link opens
 * -----------------------------------------------
 * We check the link is still good BEFORE showing the form, so nobody types
 * out a new password only to be told it expired.
 */

import { useEffect, useState } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { KeyRound, CheckCircle2 } from 'lucide-react';

import { authApi } from '../services/api';
import Loader from '../components/Loader';
import { useI18n } from '../hooks/useI18n';
import PasswordInput from '../components/PasswordInput';

export default function ResetPassword() {
  const { t } = useI18n();
  const { token } = useParams();
  const navigate = useNavigate();

  const [checking, setChecking] = useState(true);
  const [valid, setValid] = useState(false);
  const [firstName, setFirstName] = useState('');

  const [password, setPassword] = useState('');
  const [confirm, setConfirm] = useState('');
  const [error, setError] = useState('');
  const [done, setDone] = useState(false);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    authApi
      .checkResetToken(token)
      .then((data) => {
        setValid(true);
        setFirstName(data.firstName || '');
      })
      .catch((err) => {
        setValid(false);
        setError(err.message);
      })
      .finally(() => setChecking(false));
  }, [token]);

  const handleSubmit = async (event) => {
    event.preventDefault();
    setError('');

    if (password !== confirm) {
      setError(t('reset.mismatch'));
      return;
    }
    if (password.length < 8 || !/[0-9]/.test(password)) {
      setError(t('reset.tooWeak'));
      return;
    }

    setBusy(true);
    try {
      await authApi.resetPassword(token, password);
      setDone(true);
      setTimeout(() => navigate('/login'), 2500);
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  };

  if (checking) return <Loader label={t('common.checkingLink')} />;

  return (
    <div className="auth-layout">
      <div className="page-header text-center">
        <h1>{done ? t('reset.titleDone') : t('reset.title')}</h1>
        {!done && valid && firstName && <p>{t('common.welcomeBack', { name: firstName })}</p>}
      </div>

      <div className="card">
        {done ? (
          <>
            <div className="alert alert-success">
              <CheckCircle2 size={20} aria-hidden="true" />
              <div>{t('reset.done')}</div>
            </div>
            <Link to="/login" className="btn btn-primary btn-block" style={{ marginTop: 'var(--space-sm)' }}>
              {t('reset.logInNow')}
            </Link>
          </>
        ) : !valid ? (
          <>
            <div className="alert alert-error">{error}</div>
            <Link to="/forgot-password" className="btn btn-primary btn-block" style={{ marginTop: 'var(--space-sm)' }}>
              {t('reset.requestNewLink')}
            </Link>
          </>
        ) : (
          <form className="form" onSubmit={handleSubmit}>
            {error && <div className="alert alert-error">{error}</div>}

            <div className="field">
              <label htmlFor="password">{t('reset.newPassword')}</label>
              <PasswordInput
                id="password"
                value={password}
                onChange={(event) => setPassword(event.target.value)}
                autoComplete="new-password"
                required
                autoFocus
              />
              <span className="hint">{t('reset.rule')}</span>
            </div>

            <div className="field">
              <label htmlFor="confirm">{t('reset.again')}</label>
              <PasswordInput
                id="confirm"
                name="confirm"
                value={confirm}
                onChange={(event) => setConfirm(event.target.value)}
                autoComplete="new-password"
                required
              />
            </div>

            <button type="submit" className="btn btn-primary btn-block" disabled={busy}>
              <KeyRound size={18} />
              {busy ? t('common.saving') : t('reset.submit')}
            </button>
          </form>
        )}
      </div>
    </div>
  );
}
